import React, { useEffect, useRef, useState } from 'react';
import { useMutate } from 'restful-react';
import { Dropdown } from '../../FormComponents/Dropdown/Dropdown';
import { TextInput } from '../../FormComponents/TextInput/TextInput';
import { BaseModal } from '../BaseModal/BaseModal';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './DuplicateProjectDetailsModal.scss';
import { Button } from '../../FormComponents/Buttons/Button';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { ValidationError } from '../../FormComponents/ValidationError/ValidationError';
import { durationInMinutes, timezones } from '../../../common/constants';
import history from '../../../tools/history';
import {
  formatDate,
  getBaseUtcOffset,
  getTimezoneName,
  isDaylightSavings,
  isValidTime,
  isValidTimeZone,
  mergeDateAndTime,
} from '../../../helpers/dateFormatHelper/dateFormatHelper';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import { Project } from '../../../types/Project';
import { errorMessageToTranslationKey } from '../../../helpers/errorMessageHelper/errorMessageHelper';
import moment from 'moment';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
// import AutoStartInput from '../../InputComponents/AutoStartInput/AutoStartInput';

type Props = {
  isOpen: boolean;
  contentLabel: string;
  project?: Project | null;
  projectId?: string;
  projectName?: string;
  handleCloseModal: () => void;
  streamKey: boolean;
  destinations: boolean;
  captioners: boolean;
  connectors: boolean;
};

type DuplicateProjectFormData = {
  id: string;
  name: string;
  scheduledDateTime: Date | string;
  scheduledTime: string;
  scheduledTimeZone: string;
  scheduledDuration: string;
  autoStart: boolean;
  projectId?: string;
  duplicateStreamKey: boolean | undefined;
  duplicateDestinations: boolean | undefined;
  duplicateCaptioners: boolean | undefined;
  duplicateConnectors: boolean | undefined;
  duplicateGlossaries: boolean | undefined;
  duplicateOverlay: boolean | undefined;
  duplicateAggregator: boolean | undefined;
  duplicateTranscoder: boolean | undefined;
  duplicateStreamTextEventName: boolean | undefined;
};

export const DuplicateProjectDetailsModal = (props: Props) => {
  const date = props.project?.scheduledDateTime || new Date();
  const time = props.project?.scheduledTime || '';

  const defaultValues: DuplicateProjectFormData = {
    id: props.project?.id || '',
    name: props.project?.name || props.projectName || '',
    scheduledTimeZone: getTimezoneName(
      date,
      time,
      props.project?.scheduledTimeZone || 'GMT Standard Time',
    ),
    scheduledDateTime: date,
    scheduledTime: time,
    scheduledDuration: props.project?.duration.toString() || '60',
    projectId: props.projectId,
    duplicateStreamKey: props.streamKey,
    duplicateDestinations: props.destinations,
    duplicateCaptioners: props.captioners,
    duplicateConnectors: props.connectors,
    duplicateGlossaries: true,
    duplicateOverlay: true,
    duplicateAggregator: true,
    duplicateTranscoder: true,
    duplicateStreamTextEventName: true,
    autoStart: false,
  };

  const { t } = useTranslation();
  const { control, errors, handleSubmit, register } = useForm<DuplicateProjectFormData>({
    defaultValues,
  });

  const [loadingCreate, setLoadingCreate] = useState(false);
  const timezoneRef = useRef(defaultValues.scheduledTimeZone);
  const dateRef = useRef(defaultValues.scheduledDateTime);
  const timeRef = useRef(defaultValues.scheduledTime);
  const [isError, setIsError] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSuccessfulCreate = (body: DuplicateProjectFormData, data: any) => {
    if (data.statusCode === 201) {
      props.handleCloseModal();
      history.push('/producer/' + data.result);
    }
  };

  const { mutate: duplicateProject, error: errorCreate } = useMutate<DuplicateProjectFormData>({
    verb: 'POST',
    path: 'projects/duplicate',
    onMutate: onSuccessfulCreate,
  });

  useEffect(() => {
    setIsError(errorCreate !== null && errorCreate !== undefined);
  }, [errorCreate]);

  const onSubmit: SubmitHandler<DuplicateProjectFormData> = (
    data: DuplicateProjectFormData,
    event?: React.BaseSyntheticEvent,
  ) => {
    setLoadingCreate(true);
    event?.preventDefault();

    duplicateProject(data);
  };

  const getDate = (): string =>
    mergeDateAndTime(formatDate(moment(dateRef.current).format('YYYY-MM-DD')), timeRef.current);

  const parseOffset = (offsetStr: string) => {
    const [hours, minutes, seconds] = offsetStr.split(':').map(Number);
    return hours + minutes / 60 + seconds / 3600;
  };
  
  const getTimezones = () => {  
    const summerSet = new Set(
      timezones
        .filter(tz => tz.isDaylightSavings)
        .map(tz => tz.value.replace(' Summer Time', ' Standard Time'))
    );

    const isCurrentlyDaylightSavings = isDaylightSavings(getDate(), getBaseUtcOffset('GMT Standard Time'));
  
    return timezones
      .filter(tz =>
        !isCurrentlyDaylightSavings
          ? !tz.isDaylightSavings
          : tz.isDaylightSavings || !summerSet.has(tz.value)
      )
      .sort((a, b) => parseOffset(a.baseUtcOffset) - parseOffset(b.baseUtcOffset));
  };  

  const changeTimezone = (): void => {
    const date = getDate();

    if (isDaylightSavings(date, getBaseUtcOffset('GMT Standard Time'))) {
      timezoneRef.current = timezoneRef.current.replace('Standard', 'Summer');
    } else {
      timezoneRef.current = timezoneRef.current.replace('Summer', 'Standard');
    }

    control.setValue('scheduledTimeZone', '');
    control.setValue('scheduledTimeZone', timezoneRef.current);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isOverlapException = (errorCreate: any): boolean => {
    if (errorCreate.data.errors[0] === 'Project cannot be created as it overlaps with another') {
      return true;
    }

    return false;
  };

  const handleErrorClose = (): void => {
    setIsError(false);
    setLoadingCreate(false);
  };

  if (isError) {
    if (isOverlapException(errorCreate)) {
      return (
        <ErrorMessage
          message={t('error-messages.project-overlap')}
          handleErrorClose={handleErrorClose}
        />
      );
    }
    return (
      <ErrorMessage
        message={t(errorMessageToTranslationKey(errorCreate))}
        handleErrorClose={handleErrorClose}
      />
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const closeHandler = () => {
    if (!isError) {
      props.handleCloseModal();
    }
  };

  return (
    <BaseModal
      isOpen={props.isOpen}
      contentLabel={props.contentLabel}
      handleCloseModal={closeHandler}
      ariaHideApp={true}
      title={'create-project.modal-title-duplicate'}
    >
      <form className="duplicate-project-form" onSubmit={handleSubmit(onSubmit)}>
        <input
          name="id"
          defaultValue={defaultValues.id}
          type="hidden"
          ref={() => register({ name: 'id' })}
        />
        <input
          name="projectId"
          defaultValue={defaultValues.projectId}
          type="hidden"
          ref={() => register({ name: 'projectId' })}
        />
        <Controller
          name="name"
          control={control}
          rules={{ required: true, maxLength: 150 }}
          render={({ onChange, value }) => (
            <TextInput
              placeholder="create-project.project-title-placeholder"
              value={value}
              onChange={onChange}
              onAfterChange={null}
              errorType={errors.name}
              errorMessageKey={
                value.length <= 150
                  ? 'create-project.missing-title'
                  : 'create-project.title-max-length'
              }
            />
          )}
        />
        <div className="columns">
          <div className="column">
            <ValidationError
              errorType={errors.scheduledDateTime}
              errorMessageKey="create-project.missing-date"
            />
            <Controller
              name="scheduledDateTime"
              control={control}
              rules={{ required: true }}
              render={({ onChange, value }) => (
                <Calendar
                  onChange={(data) => {
                    dateRef.current = formatDate(data as Date);
                    onChange(data);
                    changeTimezone();
                  }}
                  minDate={new Date()}
                  minDetail="year"
                  next2Label={null}
                  prev2Label={null}
                  value={value ? new Date(value) : new Date()}
                />
              )}
            />
          </div>
          <div className="column">
            <ValidationError
              errorType={errors.scheduledTime}
              errorMessageKey={errors.scheduledTime?.message ?? ''}
            />
            <Controller
              name="scheduledTime"
              control={control}
              rules={{
                required: 'create-project.missing-time',
                validate: (value) => isValidTime(value, getDate(), timezoneRef.current),
              }}
              render={({ onChange, value }) => (
                <input
                  type="time"
                  onChange={(data) => {
                    timeRef.current = data.currentTarget.value;
                    onChange(data);
                    changeTimezone();
                  }}
                  className={'input timepicker' + (errors.scheduledTime ? ' is-danger' : '')}
                  id="timepicker"
                  aria-label="start time"
                  name="timepicker"
                  value={value}
                ></input>
              )}
            />
            <Controller
              control={control}
              name="scheduledTimeZone"
              className="mb-3"
              rules={{ required: true, validate: (value) => isValidTimeZone(value) }}
              render={({ onChange, value }) => (
                <Dropdown
                  value={value || getTimezoneName(getDate(), timeRef.current, value)}
                  options={getTimezones()}
                  onChange={(data) => {
                    timezoneRef.current = data?.value;
                    onChange(data?.value);
                  }}
                  placeholderKey="create-project.timezone-placeholder"
                  errorType={errors.scheduledTimeZone}
                  errorMessageKey="create-project.missing-timezone"
                />
              )}
            />
            <Controller
              control={control}
              name="scheduledDuration"
              className="mb-3"
              rules={{ required: true, min: durationInMinutes[0].value }}
              render={({ onChange, value }) => (
                <Dropdown
                  value={value || '60'}
                  options={durationInMinutes}
                  onChange={(data) => onChange(data?.value)}
                  placeholderKey="create-project.duration-placeholder"
                  errorType={errors.scheduledDuration}
                  errorMessageKey="create-project.missing-duration"
                />
              )}
            />
            {/* <Controller
              control={control}
              name="autoStart"
              className="mb-3"
              rules={{ required: false }}
              render={({ onChange, value }) => (
                <AutoStartInput
                  value={value}
                  onChange={(data, event) => {
                    onChange(data);
                    event?.preventDefault();
                  }} />
              )}
            /> */}
          </div>
        </div>
        <div className="columns">
          <div className="column shutoff-notice">
            <p>{t('create-project.auto-shutoff-1')}</p>
          </div>
          {
            <>
              <div className="column">
                <h3 className="ReactModal__Content__Body__SubTitle">
                  {t('create-project.duplicate.copy-title')}
                </h3>
                <Controller
                  control={control}
                  name="duplicateStreamKey"
                  render={({ onChange }) => (
                    <label>
                      <input
                        type="checkbox"
                        defaultChecked={defaultValues.duplicateStreamKey}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                      <span>{t('create-project.duplicate.copy-streamkey')}</span>
                    </label>
                  )}
                />
                {props.destinations ? (
                  <Controller
                    control={control}
                    name="duplicateDestinations"
                    render={({ onChange }) => (
                      <label>
                        <input
                          type="checkbox"
                          defaultChecked={defaultValues.duplicateDestinations}
                          onChange={(e) => onChange(e.target.checked)}
                        />
                        <span>{t('create-project.duplicate.copy-stream-destinations')}</span>
                      </label>
                    )}
                  />
                ) : (
                  ''
                )}
                {props.captioners ? (
                  <>
                    <Controller
                      control={control}
                      name="duplicateCaptioners"
                      render={({ onChange }) => (
                        <label>
                          <input
                            type="checkbox"
                            defaultChecked={defaultValues.duplicateCaptioners}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                          <span>{t('create-project.duplicate.copy-captioners')}</span>
                        </label>
                      )}
                    />
                  </>
                ) : (
                  ''
                )}

                {props.connectors ? (
                  <>
                    <Controller
                      control={control}
                      name="duplicateConnectors"
                      render={({ onChange }) => (
                        <label>
                          <input
                            type="checkbox"
                            defaultChecked={defaultValues.duplicateConnectors}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                          <span>{t('create-project.duplicate.copy-connectors')}</span>
                        </label>
                      )}
                    />
                  </>
                ) : (
                  ''
                )}

                <Controller
                  control={control}
                  name="duplicateGlossaries"
                  render={({ onChange }) => (
                    <label>
                      <input
                        type="checkbox"
                        defaultChecked={defaultValues.duplicateGlossaries}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                      <span>{t('create-project.duplicate.copy-glossaries')}</span>
                    </label>
                  )}
                />

                <Controller
                  control={control}
                  name="duplicateOverlay"
                  render={({ onChange }) => (
                    <label>
                      <input
                        type="checkbox"
                        defaultChecked={defaultValues.duplicateOverlay}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                      <span>{t('create-project.duplicate.copy-overlay')}</span>
                    </label>
                  )}
                />

                <Controller
                  control={control}
                  name="duplicateAggregator"
                  render={({ onChange }) => (
                    <label>
                      <input
                        type="checkbox"
                        defaultChecked={defaultValues.duplicateAggregator}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                      <span>{t('create-project.duplicate.copy-aggregator')}</span>
                    </label>
                  )}
                />

                <Controller
                  control={control}
                  name="duplicateTranscoder"
                  render={({ onChange }) => (
                    <label>
                      <input
                        type="checkbox"
                        defaultChecked={defaultValues.duplicateTranscoder}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                      <span>{t('create-project.duplicate.copy-transcoder')}</span>
                    </label>
                  )}
                />

                <Controller
                  control={control}
                  name="duplicateStreamTextEventName"
                  render={({ onChange }) => (
                    <label>
                      <input
                        type="checkbox"
                        defaultChecked={defaultValues.duplicateStreamTextEventName}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                      <span>{t('create-project.duplicate.copy-streamtext-event-name')}</span>
                    </label>
                  )}
                />
              </div>
            </>
          }
        </div>
        {!loadingCreate ? (
          <Button
            translationKey="create-project.submit"
            variant="primary"
            color="blue"
            onClick={() => null}
            type="submit"
            disabled={loadingCreate}
          />
        ) : (
          <LoadingSpinner />
        )}
      </form>
    </BaseModal>
  );
};
